// src/pages/cuentas-bancarias/CuentasBancarias.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import api from 'api/api'

import TopHeader from 'components/TopHeader'
import { normalizarBooleano } from 'utils/general'

import NuevaCuenta from './NuevaCuenta'
import EditarCuenta from './EditarCuenta'
import EliminarCuenta from './EliminarCuenta'

import './cuentas-bancarias.css'

function CuentasBancarias() {
	const [loading, setLoading] = useState(false)
	const [cuentas, setCuentas] = useState([])

	const [accionActual, setAccionActual] = useState({
		cuenta: null,
		accion: ''
	})

	// Estado para drag & drop
	const [draggedItem, setDraggedItem] = useState(null)
	const [dropTarget, setDropTarget] = useState(null)

	// Cargar cuentas
	const refreshCuentas = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('a/cuentas-bancarias')
			setCuentas(datos || [])
		} catch (error) {
			toast.error('Error al cargar cuentas bancarias')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Toggle estado activo/inactivo
	const toggleEstadoCuenta = async (id, nuevoEstado) => {
		try {
			const { msg } = await api.put(`a/cuentas-bancarias/${id}`, { activo: nuevoEstado ? 1 : 0 })
			refreshCuentas()
			toast.success(msg || 'Estado actualizado')
		} catch (error) {
			toast.error('Error al actualizar estado')
			console.error(error)
		}
	}

	// CRUD operations
	const nuevaCuenta = async (data) => {
		try {
			const { msg } = await api.post('a/cuentas-bancarias', data)
			refreshCuentas()
			cerrarAccion()
			toast.success(msg || 'Cuenta creada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al crear la cuenta')
		}
	}

	const editarCuenta = async (data) => {
		try {
			const { msg } = await api.put(`a/cuentas-bancarias/${accionActual.cuenta.id}`, data)
			refreshCuentas()
			cerrarAccion()
			toast.success(msg || 'Cuenta actualizada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al actualizar la cuenta')
		}
	}

	const eliminarCuenta = async (id) => {
		try {
			const { msg } = await api.delete(`a/cuentas-bancarias/${id}`)
			refreshCuentas()
			cerrarAccion()
			toast.success(msg || 'Cuenta eliminada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al eliminar la cuenta')
		}
	}

	// Cambiar orden (drag & drop)
	const cambiarOrden = async (cuentaId, nuevoOrden) => {
		try {
			await api.put(`a/cuentas-bancarias/${cuentaId}/orden`, { orden: nuevoOrden })
			refreshCuentas()
		} catch (error) {
			toast.error('Error al actualizar el orden')
			console.error(error)
		}
	}

	// Handlers para drag & drop
	const handleDragStart = (e, cuenta) => {
		setDraggedItem(cuenta)
		e.dataTransfer.effectAllowed = 'move'
	}

	const handleDragOver = (e, cuenta) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'

		if (draggedItem && draggedItem.id !== cuenta.id) {
			setDropTarget(cuenta.id)
		}
	}

	const handleDragLeave = () => {
		setDropTarget(null)
	}

	const handleDrop = async (e, targetCuenta) => {
		e.preventDefault()

		if (!draggedItem || draggedItem.id === targetCuenta.id) {
			setDraggedItem(null)
			setDropTarget(null)
			return
		}

		await cambiarOrden(draggedItem.id, targetCuenta.orden)

		setDraggedItem(null)
		setDropTarget(null)
	}

	const handleDragEnd = () => {
		setDraggedItem(null)
		setDropTarget(null)
	}

	useEffect(() => {
		refreshCuentas()
	}, [])

	const cerrarAccion = () => {
		setAccionActual({ cuenta: null, accion: '' })
	}

	const accionesActuales = {
		handleEditar: (cuenta) => setAccionActual({ cuenta, accion: 'editar' }),
		handleEliminar: (cuenta) => setAccionActual({ cuenta, accion: 'eliminar' })
	}

	// render
	return (
		<Container fluid id="cuentas-bancarias" className="vh-100">
			<TopHeader
				iconName="building-columns"
				text="Cuentas Bancarias"
			/>

			{/* Info */}
			<Row className="mb-4">
				<Col>
					<p className="text-muted mb-0">
						<i className="fa fa-info-circle me-2"></i>
						Las cuentas bancarias activas se mostrarán a los clientes cuando seleccionen el método de pago por transferencia bancaria.
					</p>
				</Col>
			</Row>

			{/* Botón Nueva cuenta */}
			<Row className="mb-3">
				<Col>
					<Button variant="primary" onClick={() => setAccionActual({ cuenta: null, accion: 'nueva' })}>
						<i className="fa fa-plus"></i> Nueva cuenta
					</Button>
				</Col>
			</Row>

			{/* Tabla de cuentas */}
			<Row>
				<Col>
					<div className="cuentas-tabla">
						<small>Total cuentas: {loading ? (<Spinner animation="border" size="sm" className="ms-2" />) : (<strong>{cuentas.length}</strong>)}</small>

						<Table striped bordered hover className="mt-2 tabla-cuentas">
								<thead>
									<tr>
										<th className="drag-handle-header"></th>
										<th className="banco-header">Banco</th>
										<th className="numero-header">Número de Cuenta</th>
										<th className="tipo-header">Tipo</th>
										<th className="titular-header">Titular</th>
										<th className="estado-header">Estado</th>
										<th className="acciones-header">Acciones</th>
									</tr>
								</thead>
								<tbody>
									{loading ? (
										<tr>
											<td colSpan="7" className="text-center py-4">
												<Spinner animation="border" variant="primary" />
											</td>
										</tr>
									) : (
										cuentas.length > 0 ? cuentas.map((cuenta) => (
											<tr
												key={cuenta.id}
												className={`cuenta-row ${draggedItem?.id === cuenta.id ? 'dragging' : ''} ${dropTarget === cuenta.id ? 'drop-target' : ''}`}
												draggable={true}
												onDragStart={(e) => handleDragStart(e, cuenta)}
												onDragOver={(e) => handleDragOver(e, cuenta)}
												onDragLeave={handleDragLeave}
												onDrop={(e) => handleDrop(e, cuenta)}
												onDragEnd={handleDragEnd}
											>
												<td className="drag-handle-cell">
													<i className="fa fa-grip-vertical drag-handle"></i>
												</td>
												<td className="banco-cell"><strong>{cuenta.banco}</strong></td>
												<td className="numero-cell">{cuenta.numero_cuenta}</td>
												<td className="tipo-cell">{cuenta.tipo_cuenta}</td>
												<td className="titular-cell">{cuenta.titular}</td>
												<td className="estado-cell">
													<Form.Check
														type="switch"
														id={`switch-${cuenta.id}`}
														checked={normalizarBooleano(cuenta.activo)}
														onChange={(e) => toggleEstadoCuenta(cuenta.id, e.target.checked)}
														label={cuenta.activo ? 'Activa' : 'Inactiva'}
													/>
												</td>
												<td className="acciones-cell">
													<Dropdown align="end">
														<Dropdown.Toggle
															variant="outline-secondary"
															size="sm"
															id={`dropdown-cuenta-${cuenta.id}`}
														>
															<i className="fa fa-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<Dropdown.Item onClick={() => accionesActuales.handleEditar(cuenta)}>
																<i className="fa fa-pencil me-2"></i>
																Editar
															</Dropdown.Item>
															<Dropdown.Divider />
															<Dropdown.Item onClick={() => accionesActuales.handleEliminar(cuenta)} className="text-danger">
																<i className="fa fa-trash me-2"></i>
																Eliminar
															</Dropdown.Item>
														</Dropdown.Menu>
													</Dropdown>
												</td>
											</tr>
										)) : (
											<tr>
												<td colSpan="7" className="text-center py-4">
													No hay cuentas bancarias registradas. Haz clic en "Nueva cuenta" para agregar una.
												</td>
											</tr>
										)
									)}
								</tbody>
						</Table>
					</div>
				</Col>
			</Row>

			{/* Componentes para acciones CRUD */}
			<NuevaCuenta
				mostrar={accionActual.accion === 'nueva'}
				guardar={nuevaCuenta}
				cancelar={cerrarAccion}
			/>

			<EditarCuenta
				mostrar={accionActual.accion === 'editar'}
				cuentaOriginal={accionActual.cuenta}
				guardar={editarCuenta}
				cancelar={cerrarAccion}
			/>

			<EliminarCuenta
				mostrar={accionActual.accion === 'eliminar'}
				cuenta={accionActual.cuenta}
				cancelar={cerrarAccion}
				eliminar={eliminarCuenta}
			/>
		</Container>
	)
}

export default CuentasBancarias
